
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import secureLocalStorage from "./storage";
import Footer from "./Footer";

const statusConfig = {
  pending: { label: "Pending", className: "pending" },
  confirmed: { label: "Confirmed", className: "confirmed" },
  rejected: { label: "Rejected", className: "rejected" },
  expired: { label: "Expired", className: "expired" },
};
const NO_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="80"><rect width="100%" height="100%" fill="#f1f1f1"/><text x="50%" y="50%" fill="#aaa" font-size="12" text-anchor="middle" dominant-baseline="middle">No logo</text></svg>'
  );
const PAGE_SIZE = 8;

const OrderDetails = () => {
  const navigate = useNavigate();
  let token = secureLocalStorage.getItem("n_token");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}website/my_order_details`,
        { headers: { token } }
      );
      if (res.data?.success) {
        setOrders(res.data.data);
      }
    } catch (error) {
      console.log("Error fetching order details", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    setPage(1); // filter badalte hi page 1 pe reset
  }, [filter]);

  const filteredOrders =
    filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const totalPages = Math.ceil(filteredOrders.length / PAGE_SIZE);
  const paginatedOrders = filteredOrders.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const counts = {
    all: orders.length,
    pending: orders.filter((o) => o.status === "pending").length,
    confirmed: orders.filter((o) => o.status === "confirmed").length,
    rejected: orders.filter((o) => o.status === "rejected").length,
    expired: orders.filter((o) => o.status === "expired").length,
  };

  return (
    <>
      <nav className="od-breadcrumb">
        <div className="container">
          <span onClick={() => navigate("/")}>Home</span> /{" "}
          <span onClick={() => navigate("/myaccount")}>My Earnings</span> /{" "}
          <b>My Order Details</b>
        </div>
      </nav>

      <section className="od-section">
        <div className="container">
          {!loading && orders.length > 0 && (
            <div className="od-filters">
              {["all", "pending", "confirmed", "rejected", "expired"].map((f) => (
                <button
                  key={f}
                  className={`od-filter-btn ${filter === f ? "active" : ""}`}
                  onClick={() => setFilter(f)}
                >
                  {f === "all" ? "All" : statusConfig[f]?.label || f}
                  <span className="od-filter-count">{counts[f]}</span>
                </button>
              ))}
            </div>
          )}

          <div className="od-box">
            {loading && <p className="od-loading">Loading...</p>}

            {!loading && orders.length === 0 && (
              <div className="od-empty-state">
                <img
                  src="/assets/images/banner/empty-orders.png"
                  alt="No orders yet"
                  className="od-empty-illustration"
                />
                <h2>This Is Where It All Happens!</h2>
                <p>
                  Once you shop via CashKaroNa, your order details will appear
                  here within 72 hours.
                </p>
                <button className="od-cta-btn" onClick={() => navigate("/")}>
                  See Best Deals
                </button>
              </div>
            )}

            {!loading && orders.length > 0 && filteredOrders.length === 0 && (
              <p className="od-empty-text">No orders found in this category.</p>
            )}

            {!loading && paginatedOrders.length > 0 && (
              <>
                <div className="od-orders-list">
                  {paginatedOrders.map((order) => {
                    const info = statusConfig[order.status] || statusConfig.pending;
                    return (
                      <div key={order.click_id} className="od-order-card">
                        <div className="od-order-logo-wrap">
                          <img
                            src={order.store_logo ? `${process.env.REACT_APP_API}uploads/${order.store_logo}` : NO_IMG}
                            alt={order.store_name}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = NO_IMG;
                            }}
                          />
                        </div>
                        <div className="od-order-info">
                          <p className="od-order-retailer">{order.store_name}</p>
                          <p className="od-order-date">Clicked on {formatDate(order.click_date)}</p>
                          <p className="od-order-ref">Ref ID: {order.reference_id}</p>
                          {order.status === "confirmed" && (
                            <p className="od-order-amount">Order Value: ₹{order.order_value}</p>
                          )}
                          {order.status === "rejected" && order.rejection_reason && (
                            <p className="od-order-reason">Reason: {order.rejection_reason}</p>
                          )}
                          {order.status === "pending" && (
                            <p className="od-order-reason">
                              Expected confirmation in {order.confirm_time} {order.confirm_unit}
                            </p>
                          )}
                          {order.status === "expired" && (
                            <p className="od-order-reason">
                              The expected confirmation time has passed. Your cashback will be added once the store confirms your order.
                            </p>
                          )}
                        </div>
                        <div className="od-order-status">
                          <span className={`od-status-badge ${info.className}`}>
                            {info.label}
                          </span>
                          {order.status === "confirmed" && (
                            <p className="od-order-cashback">₹{order.cashback_amount}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="od-pagination">
                    <button
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {idx > 0 && arr[idx - 1] !== p - 1 && <span className="od-page-dots">...</span>}
                          <button
                            className={page === p ? "active" : ""}
                            onClick={() => setPage(p)}
                          >
                            {p}
                          </button>
                        </React.Fragment>
                      ))}

                    <button
                      disabled={page === totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />

      <style jsx="true">{`
        .od-breadcrumb {
          padding: 12px 0;
          font-size: 0.9rem;
          color: #555;
        }
        .od-breadcrumb span {
          cursor: pointer;
          color: #c01e12;
        }
        .od-breadcrumb b {
          color: #333;
        }
        .od-section {
          padding: 2rem 0 4rem;
        }
        .od-filters {
          display: flex;
          gap: 10px;
          margin-bottom: 1.2rem;
          flex-wrap: wrap;
        }
        .od-filter-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 10px;
          border: 1px solid #ddd;
          background: #fff;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.85rem;
          color: #444;
          transition: all 0.2s ease;
        }
      .od-filter-btn.active {
  background: var(--primary-gradient);
  color: #fff;
  border-color: #c01e12;
}
        .od-filter-count {
          background: rgba(0,0,0,0.08);
          color: inherit;
          font-size: 0.7rem;
          padding: 1px 7px;
          border-radius: 10px;
        }
        .od-filter-btn.active .od-filter-count {
          background: rgba(255,255,255,0.25);
        }
        .od-box {
          border: 1px solid #eee;
          border-radius: 12px;
          padding: 1.2rem;
          background: #fff;
        }
        .od-loading, .od-empty-text {
          text-align: center;
          color: #777;
          padding: 2.5rem 0;
        }

        .od-empty-state {
          text-align: center;
          max-width: 480px;
          margin: 0 auto;
        }
        .od-empty-state h2 {
          font-weight: 700;
          margin: 1.5rem 0 0.5rem;
          color: #222;
        }
        .od-empty-state p {
          color: #777;
          margin-bottom: 1.8rem;
        }
        .od-empty-illustration {
          width: 100%;
          max-width: 476px;
          height: auto;
          object-fit: contain;
          margin-bottom: 0.5rem;
        }
        .od-cta-btn {
          background: #ff5a1f;
          color: #fff;
          border: none;
          padding: 0.9rem 3rem;
          border-radius: 6px;
          font-weight: 700;
          letter-spacing: 0.5px;
          cursor: pointer;
          transition: opacity 0.2s ease;
        }
        .od-cta-btn:hover {
          opacity: 0.9;
        }

        .od-orders-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .od-order-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          border: 1px solid #eee;
          border-radius: 10px;
          padding: 12px 16px;
          background: #fff;
          transition: box-shadow 0.2s ease;
        }
        .od-order-card:hover {
          box-shadow: 0 3px 12px rgba(0,0,0,0.06);
        }
        .od-order-logo-wrap {
          width: 100px;
          height: 72px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #eee;
          border-radius: 10px;
          padding: 6px;
          background: #fafafa;
        }
        .od-order-logo-wrap img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .od-order-info {
          flex: 1;
          min-width: 0;
        }
        .od-order-retailer {
          font-weight: 700;
          margin: 0 0 4px;
          color: #1a1a1a;
          font-size: 1rem;
        }
        .od-order-date {
          font-size: 0.82rem;
          color: #777;
          margin: 0 0 2px;
        }
        .od-order-ref {
          font-size: 0.75rem;
          color: #aaa;
          margin: 0 0 4px;
        }
        .od-order-amount {
          font-weight: 600;
          margin: 4px 0 0;
          color: #333;
          font-size: 0.9rem;
        }
        .od-order-reason {
          font-size: 0.8rem;
          color: #888;
          margin: 4px 0 0;
        }
        .od-order-status {
          text-align: right;
          flex-shrink: 0;
        }
        .od-status-badge {
          display: inline-block;
          padding: 5px 14px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: capitalize;
          white-space: nowrap;
        }
        .od-status-badge.pending {
          background: #fff3cd;
          color: #997404;
        }
        .od-status-badge.confirmed {
          background: #d1e7dd;
          color: #0f5132;
        }
        .od-status-badge.rejected {
          background: #f8d7da;
          color: #842029;
        }
        .od-status-badge.expired {
          background: #eee;
          color: #777;
        }
        .od-order-cashback {
          font-size: 0.95rem;
          font-weight: 700;
          color: #22c55e;
          margin: 8px 0 0;
        }

        .od-pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;
          margin-top: 1.5rem;
          flex-wrap: wrap;
        }
        .od-pagination button {
          min-width: 36px;
          height: 36px;
          border-radius: 8px;
          border: 1px solid #ddd;
          background: #fff;
          color: #444;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.85rem;
        }
        .od-pagination button.active {
          background: #2f5bff;
          color: #fff;
          border-color: #2f5bff;
        }
        .od-pagination button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .od-page-dots {
          color: #999;
          padding: 0 2px;
        }

        @media (max-width: 576px) {
          .od-order-card {
            flex-wrap: wrap;
          }
          .od-order-status {
            text-align: left;
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default OrderDetails;